/**
 * CrashDialogTestHarness - E2E test utility for CrashDialog
 *
 * This component is only active in test mode (SQL_STUDIO_TEST=1).
 * It listens for custom events to show/hide the crash dialog with mock context.
 */
import { useState, useEffect, useCallback } from 'react';
import { CrashDialog } from './CrashDialog';
import type { CrashContext, CrashDialogAction } from '../types/crashTypes';
import { useAppStore } from '../../../store/appStore';

/** Check if we're in test mode */
const isTestMode = (): boolean => {
  // Prefer preload-provided flag (stable, avoids localStorage timing races)
  if (typeof window !== 'undefined' && window.electronAPI?.testMode?.isTestMode) {
    return window.electronAPI.testMode.isTestMode();
  }

  // Fallback: localStorage flag set by E2E tests
  return localStorage.getItem('sql-studio-skip-session-dialog') === '1';
};

/**
 * Test harness component for CrashDialog
 * Only renders in test mode, listens for custom events to show dialog
 */
export function CrashDialogTestHarness() {
  const [isOpen, setIsOpen] = useState(false);
  const [context, setContext] = useState<CrashContext | null>(null);
  const setCrashReportMode = useAppStore((s) => s.setCrashReportMode);

  const handleAction = useCallback((action: CrashDialogAction) => {
    // Store the action for test verification
    // @ts-expect-error - test utility
    if (window.__TEST_CRASH_DIALOG__) {
      // @ts-expect-error - test utility
      window.__TEST_CRASH_DIALOG__.action = action;
    }

    // Close dialog on any action
    setIsOpen(false);
    setContext(null);
  }, []);

  useEffect(() => {
    // Only set up listeners in test mode
    if (!isTestMode()) return;

    const handleShowDialog = (event: CustomEvent<CrashContext>) => {
      setContext(event.detail);
      setIsOpen(true);
    };

    const handleHideDialog = () => {
      setIsOpen(false);
      setContext(null);
    };

    window.addEventListener('test-show-crash-dialog', handleShowDialog as EventListener);
    window.addEventListener('test-hide-crash-dialog', handleHideDialog);

    return () => {
      window.removeEventListener('test-show-crash-dialog', handleShowDialog as EventListener);
      window.removeEventListener('test-hide-crash-dialog', handleHideDialog);
    };
  }, []);

  // Don't render anything if not in test mode or no context
  if (!isTestMode() || !context) {
    return null;
  }

  return (
    <CrashDialog
      open={isOpen}
      context={context}
      onAction={handleAction}
      onActionWithMemory={(result) => {
        // @ts-expect-error - test utility
        if (window.__TEST_CRASH_DIALOG__) {
          // @ts-expect-error - test utility
          window.__TEST_CRASH_DIALOG__.lastRemember = result.remember;
        }
        // Mirror the production behavior: "Remember my choice" flips the
        // app-wide preference when checked.
        if (result.remember) {
          setCrashReportMode(result.action === 'send_report' ? 'always' : 'never');
        }
      }}
    />
  );
}
